import { z } from 'zod';
import { LastChanged } from './admin.js';
import { TeamsCard } from './teams.js';

/**
 * Settings › Award setup › Messages (APP-137): the wording of every email, bell line and Teams
 * card MyRewards sends, for the Stock Admin team and System Admins. Wording is plain text with
 * `{fact}`, `**bold**` and line breaks; only the fields someone changed are stored, keyed
 * `<situation>.<field>` (`last_day.email.subject`).
 */

const MESSAGE_KEY = /^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)?$/;
export const MessageKey = z.string().min(1).max(80).regex(MESSAGE_KEY);

// A little over the longest field (1,500), so a long paragraph gets the editor's own words.
export const WordingValue = z.union([z.string().max(2000), z.array(z.string().max(2000)).max(40)]);
export type WordingValue = z.infer<typeof WordingValue>;

/** `<situation>.<field>` → wording. */
export const WordingFields = z
  .record(
    z
      .string()
      .max(120)
      .regex(/^[a-z][a-z0-9_]*\.(?:email|bell|teams|frame)\.[a-zA-Z][a-zA-Z0-9_.]*$/),
    WordingValue,
  )
  .refine((fields) => Object.keys(fields).length <= 600, 'Too many fields');
export type WordingFields = z.infer<typeof WordingFields>;

export const MessageChannelKey = z.enum(['email', 'bell', 'teams']);
export type MessageChannelKey = z.infer<typeof MessageChannelKey>;

const Actor = z.object({ id: z.string(), name: z.string() });

/** Where a message stands: the standard wording, changed and published, or with a draft. */
export const MessageListItem = z.object({
  key: MessageKey,
  group: z.string(),
  name: z.string(),
  who: z.string(),
  when: z.string(),
  channels: z.array(MessageChannelKey),
  situations: z.number().int().nonnegative(),
  /** Fields whose published wording differs from the standard. */
  changedFields: z.number().int().nonnegative(),
  wordingVersion: z.number().int().positive(),
  draft: z.object({ by: Actor.nullable(), at: z.string() }).nullable(),
  /** The organization turned this message's email off in Notifications. */
  emailOff: z.boolean(),
  /**
   * APP-194: Send, whether MyRewards sends this email at all: the organization's switch
   * (`notifications.events`), the same one System administration › Notifications shows. Null for
   * an email that always goes and has no switch (the assistant's replies, feedback, Help's message
   * to the team) or a message that isn't an email.
   */
  send: z
    .object({
      on: z.boolean(),
      /**
       * Null: a switch. `always`: always sent (a delegate's entry to the Sector Lead). `digest`:
       * only in the Stock Admin daily digest. `go_live`, `manager_nudge`, `daily_digest`: set
       * elsewhere (the Go-live checklist, Elections & letters, the daily digest card).
       */
      lock: z.enum(['always', 'digest', 'go_live', 'manager_nudge', 'daily_digest']).nullable(),
      /** When on, people can't turn it off for themselves (K24). */
      required: z.boolean(),
      byDefault: z.boolean(),
      /** It asks someone to act, so turning it off says what stops. */
      asksToAct: z.boolean(),
      /** For one that asks someone to act: "People won't be emailed when {stops}". */
      stops: z.string().nullable(),
      /** The in-app switch beside it, kept as it is when Send changes. */
      inApp: z.boolean(),
    })
    .nullable(),
  /**
   * APP-194: Copy, whether the copy list is copied on this email (`notifications.copyEmails`).
   * `key` is the switch: the email's own, or its group's (the assistant's replies share one).
   * Null: never copied (feedback, Teams).
   */
  copy: z.object({ key: z.string(), on: z.boolean(), byDefault: z.boolean() }).nullable(),
});
export type MessageListItem = z.infer<typeof MessageListItem>;

export const MessageList = z.object({
  groups: z.array(z.object({ key: z.string(), label: z.string() })),
  messages: z.array(MessageListItem),
  frame: z.object({ changedFields: z.number().int().nonnegative(), canEdit: z.boolean() }),
  /** Teams posts and replies can reach people here. */
  teamsAvailable: z.boolean(),
  /** APP-194: who may change the Send and Copy switches (not while previewing). */
  switches: z.object({ canEditSend: z.boolean(), canEditCopy: z.boolean() }),
});
export type MessageList = z.infer<typeof MessageList>;

export const MessageFact = z.object({
  key: z.string(),
  label: z.string(),
  sample: z.string(),
  amount: z.boolean(),
});
export type MessageFact = z.infer<typeof MessageFact>;

export const MessageField = z.object({
  /** `email.subject`, `teams.button.approve`. */
  field: z.string(),
  channel: z.enum(['email', 'bell', 'teams', 'frame']),
  label: z.string(),
  hint: z.string(),
  kind: z.enum(['line', 'text', 'blocks', 'list']),
  required: z.boolean(),
  max: z.number().int().positive(),
  soft: z.number().int().positive().nullable(),
  noAmounts: z.boolean(),
  standard: WordingValue,
});
export type MessageField = z.infer<typeof MessageField>;

export const MessageSituation = z.object({
  key: z.string(),
  name: z.string(),
  when: z.string(),
  channels: z.array(MessageChannelKey),
  facts: z.array(MessageFact),
  fields: z.array(MessageField),
});
export type MessageSituation = z.infer<typeof MessageSituation>;

export const MessageDetail = z.object({
  key: MessageKey,
  group: z.string(),
  groupLabel: z.string(),
  name: z.string(),
  who: z.string(),
  when: z.string(),
  channels: z.array(MessageChannelKey),
  situations: z.array(MessageSituation),
  /** The row's version, for edits: 0 until anyone saves anything. */
  version: z.number().int().nonnegative(),
  wordingVersion: z.number().int().positive(),
  published: WordingFields,
  publishedBy: Actor.nullable(),
  publishedAt: z.string().nullable(),
  draft: z.object({ fields: WordingFields, by: Actor.nullable(), at: z.string() }).nullable(),
  /** False for Preview as, and for the frame unless a System Admin. */
  canEdit: z.boolean(),
  /**
   * The organization's email switch for this message, when it's a notification. `canSwitch`: this
   * person may turn it with its Send switch in the Messages list (APP-194).
   */
  email: z.object({ on: z.boolean(), required: z.boolean(), canSwitch: z.boolean() }).nullable(),
  teamsAvailable: z.boolean(),
});
export type MessageDetail = z.infer<typeof MessageDetail>;

/** The wording being edited, for one situation's preview or test. */
export const MessagePreviewRequest = z.object({
  situation: z.string().max(80),
  fields: WordingFields,
});
export type MessagePreviewRequest = z.infer<typeof MessagePreviewRequest>;

export const WordingIssueView = z.object({
  field: z.string(),
  severity: z.enum(['error', 'warning']),
  code: z.string(),
  message: z.string(),
  paragraph: z.number().int().nonnegative().nullable(),
  fix: z.object({ from: z.string(), to: z.string() }).nullable(),
});
export type WordingIssueView = z.infer<typeof WordingIssueView>;

/** One situation as each of its channels sends it, from the server's own renderer. */
export const MessagePreview = z.object({
  situation: z.string(),
  email: z
    .object({ subject: z.string(), preheader: z.string(), html: z.string(), text: z.string() })
    .nullable(),
  bell: z.object({ title: z.string(), body: z.string().nullable() }).nullable(),
  teams: TeamsCard.nullable(),
  issues: z.array(WordingIssueView),
});
export type MessagePreview = z.infer<typeof MessagePreview>;

export const MessageDraftSave = z.object({
  version: z.number().int().nonnegative(),
  fields: WordingFields,
});
export type MessageDraftSave = z.infer<typeof MessageDraftSave>;

export const MessagePublish = z.object({
  version: z.number().int().nonnegative(),
  fields: WordingFields,
  reason: z.string().trim().min(1).max(500),
});
export type MessagePublish = z.infer<typeof MessagePublish>;

export const MessageCommand = z.object({ version: z.number().int().nonnegative() });
export type MessageCommand = z.infer<typeof MessageCommand>;

/** Puts an earlier published version back as a draft, to publish with a reason. */
export const MessageRestore = z.object({
  version: z.number().int().nonnegative(),
  revisionId: z.uuid(),
});
export type MessageRestore = z.infer<typeof MessageRestore>;

export const MessageRevision = z.object({
  id: z.uuid(),
  version: z.number().int().positive(),
  action: z.enum(['save', 'discard', 'publish', 'restore']),
  wordingVersion: z.number().int().nullable(),
  fields: WordingFields,
  actor: Actor.nullable(),
  at: z.string(),
  reason: z.string().nullable(),
});
export type MessageRevision = z.infer<typeof MessageRevision>;

export const MessageHistory = z.object({
  revisions: z.array(MessageRevision),
  /** Recipients are named only for those who can open Sent mail (System Admins). */
  showsRecipients: z.boolean(),
  recentSends: z.array(
    z.object({
      id: z.uuid(),
      at: z.string(),
      recipientName: z.string().nullable(),
      status: z.string(),
      wordingVersion: z.number().int().nullable(),
    }),
  ),
});
export type MessageHistory = z.infer<typeof MessageHistory>;

export const MessageTestSent = z.object({ to: z.string(), subject: z.string() });
export type MessageTestSent = z.infer<typeof MessageTestSent>;

// ---- Copies of every email (APP-189) ----------------------------------------------------------

/**
 * Whether copies go to an entry now: `copied`; `inactive`, a person no longer active (or the
 * directory no longer lets them in); `no_address`, a person with no email address in the
 * directory; `outside_domains`, an address not in the organization's own email domains.
 */
export const MailCopyStatus = z.enum(['copied', 'inactive', 'no_address', 'outside_domains']);
export type MailCopyStatus = z.infer<typeof MailCopyStatus>;

/** `cc`: on the Cc line, everyone sees it. `bcc`: a blind copy, hidden from the other recipients. */
export const MailCopyMode = z.enum(['cc', 'bcc']);
export type MailCopyMode = z.infer<typeof MailCopyMode>;

export const MailCopyEntry = z.object({
  id: z.uuid(),
  kind: z.enum(['person', 'address']),
  mode: MailCopyMode,
  /** The person, for someone picked from the directory. */
  person: z.object({ id: z.uuid(), name: z.string(), jobTitle: z.string().nullable() }).nullable(),
  /** Where copies go: the person's address now, or the address as typed. Null: a person has none. */
  address: z.string().nullable(),
  status: MailCopyStatus,
  addedAt: z.string(),
  addedBy: z.object({ id: z.string(), name: z.string() }).nullable(),
});
export type MailCopyEntry = z.infer<typeof MailCopyEntry>;

/** Settings › Messages' copy list: the Stock Admin team and System Admins. */
export const MailCopyList = z.object({
  entries: z.array(MailCopyEntry),
  /** The most people and addresses it holds. */
  limit: z.number().int().positive(),
  /** The organization's own email domains, which a typed address must be in. */
  domains: z.array(z.string()),
  canEdit: z.boolean(),
  lastChanged: LastChanged.nullable(),
});
export type MailCopyList = z.infer<typeof MailCopyList>;

const CopyReason = z.string().trim().max(2000).optional();

/** Copy someone from the directory, or an address in one of the organization's domains. */
export const MailCopyAdd = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('person'),
      personId: z.uuid(),
      mode: MailCopyMode.default('cc'),
      reason: CopyReason,
    })
    .strict(),
  z
    .object({
      kind: z.literal('address'),
      address: z.string().trim().min(1).max(254),
      mode: MailCopyMode.default('cc'),
      reason: CopyReason,
    })
    .strict(),
]);
export type MailCopyAdd = z.infer<typeof MailCopyAdd>;
/** What a caller sends: the mode may be left out (CC). */
export type MailCopyAddInput = z.input<typeof MailCopyAdd>;

export const MailCopyRemove = z.object({ reason: CopyReason }).strict();
export type MailCopyRemove = z.infer<typeof MailCopyRemove>;

/** Copy an entry as CC or as BCC from now on (audited like adding and removing). */
export const MailCopyChange = z.object({ mode: MailCopyMode, reason: CopyReason }).strict();
export type MailCopyChange = z.infer<typeof MailCopyChange>;
